import axios, { AxiosResponse } from "axios";
import { t } from "@shared/utils/internationalization";
import { INotifier } from "./notifiers/INotifier";
import { NotifierFactory } from "./notifiers/NotifierFactory";

export interface IResponseHandler {
  processSuccess(response?: AxiosResponse, showToast?: boolean): void;
  processFailure(error: unknown, showToast?: boolean): void;
}

/**
 * Manages API response processing, leveraging a notifier to provide user feedback.
 * It supports handling of both successful operations and errors, translating API outcomes into meaningful notifications.
 * Upon initialization, it selects a notifier based on the current environment, allowing for adaptable feedback mechanisms.
 *
 * - `processSuccess` displays success messages if enabled and provided with a response.
 * - `processFailure` interprets errors, transforming HTTP status codes into understandable alerts for the user.
 *
 * Example usage:
 * ```
 * const handler = new ResponseHandler();
 * handler.processSuccess(response, true); // For success scenarios
 * handler.processFailure(error, true);    // For error handling
 * ```
 */
export class ResponseHandler implements IResponseHandler {
  private notifier: INotifier;

  /**
   * Initializes the response handler with a default notifier. The notifier is selected
   * based on the environment or a predefined default, facilitating the display of notifications.
   */
  constructor() {
    this.notifier = NotifierFactory.createNotifier();
  }

  /**
   * Handles successful API responses by optionally displaying a success notification to the user.
   * This method can be invoked after successful API requests to inform the user of the success state.
   *
   * @param response The AxiosResponse object from a successful Axios request. This parameter
   *                 is optional and, if provided, can be used to derive additional success messages.
   * @param showToast=false If true, triggers the display of a success message in a toast notification for user feedback.
   */
  public processSuccess(response?: AxiosResponse, showToast?: boolean): void {
    if (showToast && response) {
      this.notifier.success("");
    }
  }

  /**
   * Handles API request & response errors by mapping HTTP status codes to user-friendly messages.
   * Specifically, 400 errors are treated as custom validation errors with detailed messages from the backend.
   * For new error codes or additional custom handling that relates to axios, extend the case statements in this method.
   *
   * @param error The error object from an Axios request, containing either a detailed server response or indication
   *              of a failed request without a response.
   * @param showToast=false If true, triggers the display of the error message in a toast notification for user feedback.
   */
  public processFailure(error: unknown, showToast?: boolean): void {
    let errorMessage: string;

    if (axios.isAxiosError(error) && error.response) {
      // Axios error with a response (HTTP errors)
      errorMessage = ResponseHandler.getAxiosErrorMessage(error.response);
    } else if (axios.isAxiosError(error) && error.request) {
      // Axios error with request made but no response received
      errorMessage = t(
        "The request was made but no response was received. Please check your internet connection and try again."
      );
    } else {
      // Unexpected error or error not related to Axios
      errorMessage = t("An unexpected error occurred. Please try again.");
    }

    // Display a toast message if enabled
    if (showToast) {
      this.notifier.error(errorMessage);
    }
  }

  static getAxiosErrorMessage(response: AxiosResponse): string {
    const { status, data } = response;
    switch (status) {
      case 400:
        return data.detail || t("Bad Request");
      case 401:
        return t("Unauthorized. You might not be logged in, please try again!");
      case 403:
        return data.detail !== "FORBIDDEN"
          ? data.detail
          : t("Forbidden. Nice try, but you don’t have permission to be here!");
      case 404:
        return t(
          "Not Found. We can't find the page you are looking for, please try again!"
        );
      case 413:
        return t(
          "Too much data was sent to the server. Please try a smaller file!"
        );
      case 409:
        return (
          data.detail ||
          t(
            "This action conflicts with the current state. Please refresh and try again."
          )
        );
      case 429:
        return data.detail || t("Too many requests. Please slow down!");
      case 500:
        return t(
          "Internal Server Error. Whoops, looks like something broke. If the problem persists please contact us!"
        );
      case 502:
        return t(
          "Bad Gateway. Looks like something is temporarily unavailable, please try again soon! If the problem persists please contact us."
        );
      case 503:
        return t(
          "Service Unavailable. Looks like something is temporarily unavailable, please try again soon! If the problem persists please contact us."
        );
      case 504:
        return t(
          "Gateway Timeout. Too many people are on this page and it broke the internet! Please try again soon."
        );
      default:
        return t(
          "Something broke, and we are not sure what happened. Please try again - if the problem persists please contact us."
        );
    }
  }
}
